import { useLanguage } from '../context/LanguageContext';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Globe, Ship, Shield, DollarSign, Zap, Handshake } from 'lucide-react';
import { motion } from 'framer-motion';

export default function About() {
  const { t } = useLanguage();

  const reasons = [
    { icon: Globe, title: t('about.reason1Title'), text: t('about.reason1Text') },
    { icon: Ship, title: t('about.reason2Title'), text: t('about.reason2Text') },
    { icon: Shield, title: t('about.reason3Title'), text: t('about.reason3Text') },
    { icon: DollarSign, title: t('about.reason4Title'), text: t('about.reason4Text') },
  ];

  return (
    <div className="min-h-screen pt-28 md:pt-32 pb-24" data-testid="about-page">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-16 md:mb-24">
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#ED2227] block mb-3">
            {t('nav.about')}
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight uppercase text-white font-[Chivo]">
            {t('about.title')}
          </h1>
          <p className="text-base md:text-lg text-zinc-500 mt-4 max-w-2xl">
            {t('about.subtitle')}
          </p>
        </div>

        {/* Mission Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-24">
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="relative aspect-[4/5] overflow-hidden rounded-md bg-[#121212] flex items-center justify-center"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_global-auto-export-2/artifacts/3o39rs6b_Partner4cars_logoL%20wit%20%281%29.png"
                alt="Partners4Cars Logo"
                className="w-3/4 object-contain"
                data-testid="about-company-logo"
              />
            </motion.div>
          </div>

          <div className="lg:col-span-7 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white font-[Chivo] mb-6">
                  {t('about.mission')}
                </h2>
                <p className="text-zinc-400 text-base md:text-lg leading-relaxed">
                  {t('about.missionText')}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-6 pt-8 border-t border-white/5">
                {[
                  { value: '2,500+', label: 'Vehicles' },
                  { value: '45+', label: 'Countries' },
                  { value: '25+', label: 'Years' },
                ].map((stat, i) => (
                  <div key={i}>
                    <span className="text-2xl md:text-3xl font-black text-[#ED2227] font-[Chivo] block">
                      {stat.value}
                    </span>
                    <span className="text-xs font-mono uppercase tracking-widest text-zinc-600">
                      {stat.label}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Why Choose Us */}
        <div>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white font-[Chivo] mb-12 text-center">
            {t('about.whyUs')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {reasons.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-[#121212] border border-zinc-800/50 rounded-md p-8 md:p-10 group hover:border-[#ED2227]/20 transition-all duration-300"
              >
                <item.icon className="w-8 h-8 text-[#ED2227] mb-6" strokeWidth={1.5} />
                <h3 className="text-xl font-bold text-white mb-3 font-[Chivo]">{item.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Wholesale Partner */}
        <div className="mt-24 md:mt-32 pt-16 md:pt-20 border-t border-white/5" data-testid="about-wholesale-section">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 md:gap-16 items-center">
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-6"
              >
                <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#ED2227] block">
                  {t('wholesale.eyebrow')}
                </span>
                <h2 className="text-3xl md:text-4xl font-black tracking-tight text-white font-[Chivo] leading-tight">
                  {t('wholesale.title')}
                </h2>
                <div className="space-y-4 text-zinc-400 text-sm md:text-base leading-relaxed">
                  <p>{t('wholesale.p1')}</p>
                  <p>{t('wholesale.p2')}</p>
                  <p>{t('wholesale.p3')}</p>
                </div>
                <div className="flex flex-wrap gap-4 pt-4">
                  <Link to="/inventory">
                    <Button
                      className="bg-[#ED2227] text-white hover:bg-red-700 rounded-sm font-bold uppercase tracking-wider px-8 h-12 text-sm"
                      data-testid="about-wholesale-inventory-btn"
                    >
                      {t('wholesale.ctaInventory')}
                      <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white hover:text-black rounded-sm uppercase tracking-wider px-8 h-12 text-sm"
                      data-testid="about-wholesale-contact-btn"
                    >
                      {t('wholesale.ctaContact')}
                    </Button>
                  </Link>
                </div>
              </motion.div>
            </div>
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-[#121212] border border-zinc-800/50 rounded-md p-8 md:p-10 space-y-6"
              >
                {[
                  { icon: Globe, label: t('wholesale.feature1Title'), desc: t('wholesale.feature1Desc') },
                  { icon: Zap, label: t('wholesale.feature2Title'), desc: t('wholesale.feature2Desc') },
                  { icon: Handshake, label: t('wholesale.feature3Title'), desc: t('wholesale.feature3Desc') },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#ED2227]/10 rounded-sm flex items-center justify-center shrink-0">
                      <item.icon className="w-5 h-5 text-[#ED2227]" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h4 className="text-white font-bold text-sm font-[Chivo] mb-1">{item.label}</h4>
                      <p className="text-zinc-500 text-xs leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </div>

        {/* Track Record / 25+ Years */}
        <div className="mt-24 md:mt-32 pt-16 md:pt-20 border-t border-white/5" data-testid="about-experience-section">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 md:gap-16 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { value: t('experience.stat1Value'), label: t('experience.stat1Label') },
                    { value: t('experience.stat2Value'), label: t('experience.stat2Label') },
                    { value: t('experience.stat3Value'), label: t('experience.stat3Label') },
                    { value: t('experience.stat4Value'), label: t('experience.stat4Label') },
                  ].map((stat, i) => (
                    <div
                      key={i}
                      className="bg-[#121212] border border-zinc-800/50 rounded-md p-6 text-center"
                    >
                      <span className="text-3xl md:text-4xl font-black text-[#ED2227] font-[Chivo] block mb-2">
                        {stat.value}
                      </span>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 leading-tight block">
                        {stat.label}
                      </span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-6"
              >
                <span className="text-xs font-mono uppercase tracking-[0.3em] text-[#ED2227] block">
                  {t('experience.eyebrow')}
                </span>
                <h2 className="text-3xl md:text-4xl font-black tracking-tight text-white font-[Chivo] leading-tight">
                  {t('experience.title')}
                </h2>
                <div className="space-y-4 text-zinc-400 text-sm md:text-base leading-relaxed">
                  <p>{t('experience.p1')}</p>
                  <p>{t('experience.p2')}</p>
                </div>
                <ul className="space-y-3 pt-2">
                  {[
                    t('experience.bullet1'),
                    t('experience.bullet2'),
                    t('experience.bullet3'),
                    t('experience.bullet4'),
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-zinc-300 text-sm">
                      <div className="w-1.5 h-1.5 bg-[#ED2227] rounded-full shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
                <p className="text-zinc-400 text-sm md:text-base leading-relaxed">
                  {t('experience.closing')}
                </p>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Image Strip */}
        <div className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            'https://images.unsplash.com/photo-1767907571229-01cf4ba03590?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
            'https://images.unsplash.com/photo-1772990913336-39ee098566db?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
            'https://images.unsplash.com/photo-1629320029146-72ec6e79c7ed?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
          ].map((img, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              className="aspect-[16/9] overflow-hidden rounded-sm"
            >
              <img
                src={img}
                alt={`Partners4Cars ${i + 1}`}
                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
              />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
